import React, {Ref, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {IInnerWindowController, InnerWindowContext} from '../windows';
import {FloatingWindow, FloatingWindowProps, FloatingWindowRenderProps} from "../floating-window/floating-window";
import {
    assertNotNullable,
    Evaluable,
    evaluateWhenFunction,
    IdGenerator,
    iterableMapToArray
} from "@wix/devzai-utils-common";
import {reactCreateMemo, reactForwardRef} from "../react-utils/react-utils";
import {UnmountingTaskScheduler, useRenderState} from "../hooks";

export const FloatingWindowOpener = reactCreateMemo(reactForwardRef(function FloatingWindowOpener<CONTEXT extends {}> (props: FloatingWindowOpener.Props<CONTEXT>) {
    const {
        ref,
        children,
        containerInfo,
        onOpen,
        onClose
    } = props;

    const windowClosingControllerRef = useRef<FloatingWindowOpener.WindowClosingController>(null);
    const [openingContext, setOpeningContext] = useState<{
        openingId: string;
        contextProps: CONTEXT;
        openingOptions?: FloatingWindowOpener.OpeningOptions
    } | null>(null);

    const renderState = useRenderState({
        onOpen: () => {
            onOpen?.();
        },
        onClose: () => {
            onClose?.();

            openingContext?.openingOptions?.onClose?.()

            setOpeningContext(null);
        }
    })

    const floatingWindowOpener = useMemo<FloatingWindowOpener<CONTEXT>>(() => {
        return {
            open (contextProps, openingOptions) {
                setOpeningContext({
                    contextProps: contextProps,
                    openingOptions: openingOptions,
                    openingId: IdGenerator.uniqueId()
                });

                renderState.current.onOpen()

                return this;
            },
            close () {
                const windowClosingController = windowClosingControllerRef.current;
                if (windowClosingController) {
                    windowClosingController.handleWindowClosing(() => {
                        renderState.current.onClose();
                    })
                } else {
                    renderState.current.onClose();
                }
            },
            closeWindowImmediately () {
                renderState.current.onClose();
            }
        }
    }, [renderState]);

    useImperativeHandle(ref, () => floatingWindowOpener, [floatingWindowOpener])

    const innerWindowController = useMemo<IInnerWindowController>(() => {
        return {
            closeWindow: () => {
                floatingWindowOpener.close()
            },
            closeWindowImmediately: () => {
                floatingWindowOpener.closeWindowImmediately();
            }
        }
    }, [floatingWindowOpener])

    if (!openingContext) {
        return null;
    }

    return (
        <InnerWindowContext.Provider
            value={innerWindowController}
        >
            <FloatingWindowWrapper
                containerInfo={containerInfo}
                behaviours={openingContext.openingOptions?.behaviours}
                key={openingContext.openingId}
            >
                {floatingWindowRenderProps => {
                    return evaluateWhenFunction(children, {
                        ...floatingWindowRenderProps,
                        floatingWindowOpener: floatingWindowOpener,
                        contextProps: openingContext.contextProps,
                        windowClosingControllerRef: windowClosingControllerRef
                    })
                }}
            </FloatingWindowWrapper>
        </InnerWindowContext.Provider>
    );
}));

const FloatingWindowWrapper = React.memo(function FloatingWindowWrapper (
    props: FloatingWindowProps & {
        behaviours?: FloatingWindowOpener.OpenWindowBehaviour[];
    }
) {
    const {
        behaviours = [],
        ...floatingWindowProps
    } = props;

    const innerWindowController = assertNotNullable(useContext(InnerWindowContext))

    useEffect(() => {

        const behaviourContext = {
            windowController: innerWindowController
        };
        
        const destructors = iterableMapToArray(behaviours, (autoClosingBehaviour, skip) => {
            const destructor = autoClosingBehaviour(behaviourContext);

            return destructor ? destructor : skip;
        })

        return () => {
            for (const destructor of destructors) {
                destructor();
            }
        }
    }, []);

    return (
        <FloatingWindow
            {...floatingWindowProps}
        />
    );
});

export interface FloatingWindowOpener<CONTEXT extends {} = {}> {
    open (
        contextProps: CONTEXT,
        options?: FloatingWindowOpener.OpeningOptions
    ) : this;

    close () : void;

    closeWindowImmediately () : void;
}

export namespace FloatingWindowOpener {


    export interface OpenWindowBehaviourContext {
        windowController: IInnerWindowController;
    }

    export type OpenWindowBehaviour = (behaviourContext: OpenWindowBehaviourContext) => (void | (() => void));

    export interface OpeningOptions {
        behaviours?: FloatingWindowOpener.OpenWindowBehaviour[];
        onClose?: () => void;
    }

    export interface WindowClosingController {
        handleWindowClosing (closeCallback: () => void) : void;
    }
    
    export interface WindowRenderProps<CONTEXT extends {}> extends FloatingWindowRenderProps {
        floatingWindowOpener: FloatingWindowOpener<CONTEXT>;
        contextProps: CONTEXT;
        windowClosingControllerRef: React.Ref<WindowClosingController>;
    }

    export interface Props<CONTEXT extends {}> {
        ref?: Ref<FloatingWindowOpener<CONTEXT>>;
        children: Evaluable<(renderProps: WindowRenderProps<CONTEXT>) => React.ReactElement>;
        containerInfo?: FloatingWindowProps['containerInfo'];
        onOpen?: () => void;
        onClose?: () => void;
    }
}

export function floatingWindowOpenerCreateAutoCloseOnTimeoutBehaviour (
    options: {
        timeout: number;
    }
) : FloatingWindowOpener.OpenWindowBehaviour {
    
    const {
        timeout
    } = options;
    
    return (behaviourContext) => {
        const timeoutHandler = setTimeout(() => {
            behaviourContext.windowController.closeWindow()
        }, timeout)

        return () => {
            clearTimeout(timeoutHandler);
        }
    }
}

export function floatingWindowOpenerCreateAutoCloseOnComponentUnmount (
    options: {
        unmountingTaskScheduler: UnmountingTaskScheduler;
    }
) : FloatingWindowOpener.OpenWindowBehaviour {

    const {
        unmountingTaskScheduler
    } = options;

    return (behaviourContext) => {

        const task = unmountingTaskScheduler(() => {
            behaviourContext.windowController.closeWindowImmediately()
        })

        return () => {
            task.dispose();
        }
    }
}

export function floatingWindowOpenerOpenWithClosedPromise<CONTEXT extends {} = {}> (
    floatingWindowOpener: FloatingWindowOpener<CONTEXT>,
    contextProps: CONTEXT,
    options: FloatingWindowOpener.OpeningOptions = {}
) {
    const {
        onClose,
        ...restOptions
    } = options;

    return new Promise((resolve) => {
        floatingWindowOpener.open(contextProps, {
            ...restOptions,
            onClose: () => {
                onClose?.();

                resolve(undefined)
            }
        })
    })

}